/**
 * useStoreActions — encapsulates store lifecycle API calls
 * (approve / reject / request edit / deactivate / archive)
 * plus the `processingStore` busy-state.
 *
 * The hook is intentionally side-effect heavy: it closes modals, clears
 * selected store, fires toasts, then calls `refreshData()`. Centralising the
 * sequence here is what fixed the black-screen race on approve in Round 36.
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const t = (lang, ar, en) => (lang === 'ar' ? ar : en);

export default function useStoreActions({
  // Required deps
  token,
  language = 'ar',
  refreshData,
  // Modal close + reset setters
  setShowStoreDetails,
  setSelectedStore,
  setShowRejectModal,
  setShowModificationModal,
  setShowDeactivateModal,
  setRejectReason,
  setSelectedRejectReason,
  setModificationMessage,
  setSelectedModificationReason,
  setDeactivateReason,
}) {
  const [processingStore, setProcessingStore] = useState(null);

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const closeStoreModal = useCallback(() => {
    setShowStoreDetails?.(false);
    setSelectedStore?.(null);
  }, [setShowStoreDetails, setSelectedStore]);

  // ----- Approve -----
  const approveStore = useCallback(async (storeId) => {
    if (!storeId) {
      toast.error(t(language, 'هذا المتجر بدون معرّف — حدّث الصفحة وأعد المحاولة', 'This store has no ID — refresh the page and retry'));
      return;
    }
    setProcessingStore(storeId);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/stores/${storeId}/approve`, {}, { headers: headers() });
      // Close modal FIRST → then refresh, prevents black-screen race
      closeStoreModal();
      toast.success(t(language, 'تم تفعيل المتجر بنجاح', 'Store activated successfully'));
      await refreshData?.();
    } catch (err) {
      console.error('approveStore error:', err);
      toast.error(t(language, 'فشل في قبول المتجر', 'Failed to approve store'));
    } finally {
      setProcessingStore(null);
    }
  }, [language, refreshData, headers, closeStoreModal]);

  // ----- Reject -----
  const rejectStore = useCallback(async ({ store, reasonCategory, reasonDetail }) => {
    if (!store || !reasonCategory) {
      toast.error(t(language, 'يرجى اختيار سبب الرفض', 'Please select a rejection reason'));
      return;
    }
    setProcessingStore(store.id);
    try {
      const fullReason = reasonDetail ? `${reasonCategory}: ${reasonDetail}` : reasonCategory;
      await axios.put(
        `${BACKEND_URL}/api/admin/stores/${store.id}/reject`,
        { reason: fullReason },
        { headers: headers() },
      );
      setShowRejectModal?.(false);
      setRejectReason?.('');
      setSelectedRejectReason?.('');
      closeStoreModal();
      toast.success(t(language, 'تم رفض المتجر', 'Store rejected'));
      await refreshData?.();
    } catch (err) {
      console.error('rejectStore error:', err);
      toast.error(t(language, 'فشل في رفض المتجر', 'Failed to reject store'));
    } finally {
      setProcessingStore(null);
    }
  }, [language, refreshData, headers, closeStoreModal, setShowRejectModal, setRejectReason, setSelectedRejectReason]);

  // ----- Request edit -----
  const requestModification = useCallback(async ({ store, reasonCategory, reasonMessage }) => {
    if (!store || !reasonCategory) {
      toast.error(t(language, 'يرجى اختيار سبب طلب التعديل', 'Please select a modification reason'));
      return;
    }
    setProcessingStore(store.id);
    try {
      const fullReason = reasonMessage ? `${reasonCategory}: ${reasonMessage}` : reasonCategory;
      await axios.put(
        `${BACKEND_URL}/api/admin/stores/${store.id}/request-edit`,
        { reason: fullReason },
        { headers: headers() },
      );
      setShowModificationModal?.(false);
      setModificationMessage?.('');
      setSelectedModificationReason?.('');
      closeStoreModal();
      toast.success(t(language, 'تم إرسال طلب التعديل', 'Modification request sent'));
      await refreshData?.();
    } catch (err) {
      console.error('requestModification error:', err);
      toast.error(t(language, 'فشل في إرسال طلب التعديل', 'Failed to send modification request'));
    } finally {
      setProcessingStore(null);
    }
  }, [language, refreshData, headers, closeStoreModal, setShowModificationModal, setModificationMessage, setSelectedModificationReason]);

  // ----- Deactivate (stop) -----
  const deactivateStore = useCallback(async (storeId, reason) => {
    if (!storeId) return;
    setProcessingStore(storeId);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/stores/${storeId}/deactivate`,
        { reason: reason || '' },
        { headers: headers() },
      );
      setShowDeactivateModal?.(false);
      setDeactivateReason?.('');
      closeStoreModal();
      toast.success(t(language, 'تم إيقاف المتجر وإرسال إشعار للمالك', 'Store stopped and owner notified'));
      await refreshData?.();
    } catch (err) {
      console.error('deactivateStore error:', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t(language, 'فشل في إيقاف المتجر', 'Failed to stop store');
      toast.error(msg);
    } finally {
      setProcessingStore(null);
    }
  }, [language, refreshData, headers, closeStoreModal, setShowDeactivateModal, setDeactivateReason]);

  // ----- Archive -----
  const archiveStore = useCallback(async (storeId) => {
    if (!storeId) return;
    setProcessingStore(storeId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/stores/${storeId}/archive`, {}, { headers: headers() });
      closeStoreModal();
      toast.success(t(language, 'تم نقل المتجر إلى الأرشيف', 'Store moved to archive'));
      await refreshData?.();
    } catch (err) {
      console.error('archiveStore error:', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t(language, 'فشل في الأرشفة', 'Archive failed');
      toast.error(msg);
    } finally {
      setProcessingStore(null);
    }
  }, [language, refreshData, headers, closeStoreModal]);

  // ----- Cascade delete (iter-233 Feb 2026) -----
  // Calls DELETE /admin/stores/{id}/cascade → proxied to CloudGM Business
  // backend which deletes the store AND every related record (items, orders,
  // notifications, activity logs, transactions). Returns the counts so we
  // can show a nice "12 items, 3 orders" toast to the operator.
  const cascadeDeleteStore = useCallback(async (storeId) => {
    if (!storeId) {
      toast.error(t(language, 'هذا المتجر بدون معرّف', 'This store has no ID'));
      return;
    }
    setProcessingStore(storeId);
    try {
      const res = await axios.delete(
        `${BACKEND_URL}/api/admin/stores/${storeId}/cascade`,
        { headers: headers() },
      );
      closeStoreModal();
      const d = res.data?.deleted || {};
      const bits = [];
      if (d.store_items) bits.push(t(language, `${d.store_items} منتج`, `${d.store_items} items`));
      if (d.single_store_orders) bits.push(t(language, `${d.single_store_orders} طلبات`, `${d.single_store_orders} orders`));
      if (d.notifications) bits.push(t(language, `${d.notifications} إشعارات`, `${d.notifications} notifications`));
      const summary = bits.length
        ? t(language, `تم حذف المتجر (${bits.join('، ')})`, `Store deleted (${bits.join(', ')})`)
        : t(language, 'تم حذف المتجر نهائياً من الطرفين', 'Store cascade-deleted from both sides');
      toast.success(summary);
      await refreshData?.();
    } catch (err) {
      console.error('cascadeDeleteStore error:', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t(language, 'فشل الحذف النهائي', 'Cascade delete failed');
      toast.error(msg);
    } finally {
      setProcessingStore(null);
    }
  }, [language, refreshData, headers, closeStoreModal]);

  return {
    processingStore,
    setProcessingStore,
    approveStore,
    rejectStore,
    requestModification,
    deactivateStore,
    archiveStore,
    cascadeDeleteStore,
  };
}
